const initialState = {
  status: 'done',
  event: {},
  invitee: {},
  fields_count: 0,
  fields_filled: [],
  showExternalRedirect: false,
  showPhoneVerification: false,
  payment: {
    status: 'unpaid',
    data: null,
    error: null,
  },
  coupon: {
    data: null,
    error: null,
  },
  stripeClientSecret: {
    status: 'ready',
    value: null
  },
  stripePaymentElementReadyForCapture: false,
  saveFormDataForStripeRedirect: false,
  isBookingAfterStripeRedirect: false,
  savedFormDataForStripeRedirect: null,
  externalRedirectFormData: null,
  autofillData: null,
  location_configuration: {
    id: null,
    location: '',
    phone_number: '',
    additional_info: '',
    phone_field: {
      value: ''
    }
  },
  recaptcha_challenge: {
    show: false,
    failed: false,
    request_id: null
  },
  builtEventCustomFields: []
}

export default (state = initialState, action) => {
  switch (action.type) {
    case 'RETRIEVE_AUTOFILL_DATA':
      return {
        ...state,
        autofillData: action.data
      }

    case 'FORM_FIELD_MOUNTED':
      return {
        ...state,
        fields_count: state.fields_count + 1
      }

    case 'FORM_FIELD_UPDATED':
      return {
        ...state,
        fields_filled: action.fields_filled
      }

    case 'FORM_UNMOUNTED':
      return {
        ...state,
        fields_count: 0,
        fields_filled: [],
        builtEventCustomFields: []
      }

    case 'SELECT_LOCATION_CONFIGURATION':
      return {
        ...state,
        location_configuration: {
          ...state.location_configuration,
          ...action.payload.location_configuration
        }
      }

    case 'LOCATION_CHANGE':
      return {
        ...state,
        location_configuration: {
          ...state.location_configuration,
          location: action.payload.location
        }
      }

    case 'PHONE_NUMBER_CHANGE':
      return {
        ...state,
        location_configuration: {
          ...state.location_configuration,
          phone_number: action.payload.phone_number.value,
          phone_field: action.payload.phone_number,
        }
      }

    case 'SCHEDULING_VALIDATION_REQUEST':
      return {
        ...state,
        status: 'submitting'
      }

    case 'SCHEDULING_VALIDATION_DONE':
      return {
        ...state,
        // Keep status as 'submitting' - don't reset to 'done' during submission flow
        status: state.status
      }

    case 'SCHEDULING_VALIDATION_FAIL':
      return {
        ...state,
        status: 'done',
        error: {
          type: 'validation',
          payload: action.payload.errors
        }
      }

    case 'SCHEDULING_PAYMENT_REQUEST':
      return {
        ...state,
        status: 'submitting',
        payment: {
          status: 'requested',
          data: null,
          error: null
        }
      }

    case 'SCHEDULING_PAYMENT_DONE':
      return {
        ...state,
        // Keep status as is - payment completion is tracked separately
        status: state.status,
        payment: {
          status: 'paid',
          data: action.payload
        }
      }

    case 'SCHEDULING_PAYMENT_ERROR':
      return {
        ...state,
        status: 'done',
        payment: {
          status: 'unpaid',
          error: action.payload.message
        }
      }

    case 'SCHEDULING_PAYMENT_RESET':
      return {
        ...state,
        status: 'done',
        payment: {
          status: 'unpaid',
          data: null
        }
      }

    case 'SCHEDULING_REQUEST':
      return {
        ...state,
        status: 'submitting',
        showExternalRedirect: false,
        showPhoneVerification: false,
        externalRedirectFormData: null,
        recaptcha_challenge: {
          ...state.recaptcha_challenge,
          failed: false
        }
      }

    case 'SCHEDULING_DONE':
      return {
        ...state,
        status: 'done',
        event: action.payload.event,
        invitee: action.payload,
        payment: {
          status: 'unpaid',
          data: null,
          error: null
        },
        recaptcha_challenge: {
          show: false,
          failed: false
        }
      }

    case 'SCHEDULING_SHOW_EXTERNAL_REDIRECT': {
      return {
        ...state,
        showExternalRedirect: true,
        externalRedirectFormData: null,
      }
    }

    case 'SCHEDULING_SHOW_PHONE_VERIFICATION':
      return {
        ...state,
        showPhoneVerification: true,
        externalRedirectFormData: action.payload?.formData || null,
      }

    case 'SCHEDULING_FINISH_PHONE_VERIFICATION':
      return {
        ...state,
        showPhoneVerification: false,
        showExternalRedirect: true,
        externalRedirectFormData: null,
      }

    case 'INTERCOM_SCHEDULING_DONE':
      return {
        ...state,
        is_submitting_intercom: true,
        status: 'submitting'
      }

    case 'SCHEDULING_FAIL':
      return {
        ...state,
        status: 'done',
        showExternalRedirect: false,
        showPhoneVerification: false,
        error: action.payload,
        payment: {
          ...state.payment,
          // If scheduling fails, make sure we pull a new payment method
          status: 'unpaid',
          error: action.payload.payload && action.payload.payload.payment && action.payload.payload.payment.message
        }
      }

    case 'DISMISS_SCHEDULE_VALIDATION_ERROR':
      if (!(state.error && state.error.type === 'validation')) return state
      if (
        !(
          state.error.payload
          && state.error.payload[action.payload.model]
          && state.error.payload[action.payload.model][action.payload.attribute]
        )
      ) {
        return state
      }

      return {
        ...state,
        error: {
          type: state.error.type,
          payload: {
            ...state.error.payload,
            [action.payload.model]: {
              ...state.error.payload[action.payload.model],
              [action.payload.attribute]: undefined
            }
          }
        }
      }

    case 'DISMISS_SCHEDULE_ERROR':
      return {
        ...state,
        status: 'done',
        error: null
      }

    case 'POLL_VOTES_REQUEST':
      return {
        ...state,
        status: 'submitting'
      }

    case 'POLL_VOTES_SUCCESS':
      return {
        ...state,
        status: 'done',
        invitee: action.payload,
        event: {
          scheduled_profile: action.payload.scheduled_profile,
          publishers_list: action.payload.publishers_list
        }
      }

    case 'SHOW_RECAPTCHA_CHALLENGE':
      return {
        ...state,
        status: 'done',
        recaptcha_challenge: {
          show: true,
          failed: false,
          request_id: action.payload?.requestId ?? null
        }
      }

    case 'DISMISS_RECAPTCHA_CHALLENGE':
      return {
        ...state,
        status: 'done',
        recaptcha_challenge: {
          show: false,
          failed: false,
          request_id: null
        }
      }

    case 'RECAPTCHA_CHALLENGE_FAILED':
      return {
        ...state,
        status: 'done',
        recaptcha_challenge: {
          ...state.recaptcha_challenge,
          show: true,
          failed: true
        }
      }

    case 'COUPON_ERROR':
      return {
        ...state,
        status: 'done',
        coupon: {
          data: action.payload,
          error: true
        }
      }

    case 'RESET_COUPON_ERROR':
      return {
        ...state,
        coupon: {
          data: null,
          error: null
        }
      }

    case 'SHOW_VERIFICATION_CODE_CHALLENGE':
      return {
        ...state,
        verification_code_challenge: {
          show: true,
          scheduling_params: action.payload.schedulingParams,
          error_message: action.payload.errorMessage
        },
      }

    case 'RESET_VERIFICATION_CODE_CHALLENGE':
      return {
        ...state,
        verification_code_challenge: {
          show: false
        },
      }

    case 'CUSTOM_FIELD_INITIAL_SET':
      return {
        ...state,
        builtEventCustomFields: action.payload
      }

    case 'CUSTOM_FIELD_UPDATE':
      return {
        ...state,
        builtEventCustomFields: action.payload
      }

    case 'SET_STRIPE_CLIENT_SECRET':
      return {
        ...state,
        stripeClientSecret: action.payload
      }

    case 'SET_STRIPE_PAYMENT_ELEMENT_READY_FOR_CAPTURE':
      return {
        ...state,
        stripePaymentElementReadyForCapture: action.payload ?? true
      }

    case 'SET_SAVE_FORM_DATA_FOR_STRIPE_REDIRECT':
      return {
        ...state,
        saveFormDataForStripeRedirect: action.payload
      }

    case 'SET_SAVED_FORM_DATA_AFTER_STRIPE_REDIRECT':
      return {
        ...state,
        savedFormDataForStripeRedirect: action.payload
      }

    case 'SET_IS_BOOKING_AFTER_STRIPE_REDIRECT':
      return {
        ...state,
        isBookingAfterStripeRedirect: action.payload
      }

    default:
      return state
  }
}
